import { BreakpointObserver } from '@angular/cdk/layout';
import { CdkPortalOutlet, PortalModule } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SharedModule } from 'primeng/api';
import { DrawerModule } from 'primeng/drawer';
import { map } from 'rxjs/operators';
import { DrawerService } from './fcp-drawer.service';

type PrimeDrawerPosition = 'left' | 'right' | 'top' | 'bottom' | 'full';

/** Matches viewports below `--breakpoint-md` (768px). */
const MOBILE_MEDIA_QUERY = '(max-width: 767.98px)';

@Component({
    selector: 'fcp-drawer',
    imports: [CommonModule, DrawerModule, PortalModule, CdkPortalOutlet, SharedModule],
    styleUrl: './fcp-drawer.component.less',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.data-e2e-hook]': 'e2e()'
    },
    template: `
        <p-drawer
            [visible]="visible()"
            (visibleChange)="handleVisibleChange($event)"
            [position]="effectivePosition()"
            [closable]="showCloseIcon()"
            [styleClass]="drawerStyleClass()"
            [closeOnEscape]="true"
            [dismissible]="true"
            [modal]="true"
            [autoZIndex]="true"
        >
            <ng-template #closeicon>
                <i [class]="closeIconClass" aria-hidden="true"></i>
            </ng-template>

            @if (contentPortal(); as portal) {
                <ng-container *cdkPortalOutlet="portal"></ng-container>
            } @else {
                <ng-content></ng-content>
            }
        </p-drawer>
    `
})
export class FcpDrawerComponent {
    private drawerService = inject(DrawerService);
    private breakpointObserver = inject(BreakpointObserver);

    readonly e2e = this.drawerService.e2eHook;
    readonly visible = this.drawerService.drawerVisible;
    readonly contentPortal = this.drawerService.contentPortal;
    readonly showCloseIcon = this.drawerService.showCloseIcon;
    readonly size = this.drawerService.drawerSize;
    readonly position = this.drawerService.drawerPosition;

    /**
     * On mobile, small/medium/large all collapse to PrimeNG's full-screen layout
     * (position='full') rather than being forced to 100vw via CSS. This lets
     * PrimeNG own the full-screen chrome instead of us reimplementing it.
     */
    private readonly isMobile = toSignal(
        this.breakpointObserver.observe(MOBILE_MEDIA_QUERY).pipe(map((state) => state.matches)),
        { initialValue: this.breakpointObserver.isMatched(MOBILE_MEDIA_QUERY) }
    );

    readonly effectivePosition = computed<PrimeDrawerPosition>(() => {
        if (this.size() === 'full-screen' || this.isMobile()) {
            return 'full';
        }
        return this.position();
    });

    readonly drawerStyleClass = computed(() => `fcp-drawer fcp-drawer-size-${this.size()}`);

    readonly closeIconClass = 'fc-font-icon fc-font-icon-close';

    handleVisibleChange(visible: boolean): void {
        if (!visible) {
            this.drawerService.close();
        }
    }
}
